/**
 * All tables have save logic for search, sorting and pagination, based on location query params.
 * Most differences in url to POST API queries and default chips
 *
 * TODO
 * - a lot of code duplication in table headers, same inputs, same styles. Maybe create builder?
 */

import {
  DealsMode,
  ItemType,
  OfferStatus,
  OperType,
  QueryParam,
  RoleId,
} from 'common/types';
import {useCurrentUser} from 'hooks/user';
import {isNaN, toNumber} from 'lodash';
import debounce from 'lodash/debounce';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {parseLocationSearch} from 'utils/navigation';

const FIRST_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const PAGE_SIZE_OPTIONS_MOBILE = [25, 50];
export const PARAM_UNKNOWN_VALUE = '';

export enum TableSearchParam {
  Page = 'page',
  PageSize = 'limit',
  Sort = 'sort',
  Address = 'address',
  OperType = 'operType',
  Mode = 'mode',
  SubMode = 'subMode',
  Id = 'id',
  Rieltor = 'rieltor',
  RieltorName = 'rieltorName',
  Office = 'office',
  ItemType = 'itemType',
  CityName = 'cityName',
  CityId = 'cityId',
  RegionName = 'regionName',
  RegionId = 'regionId',
  City = 'city',
  DistrictName = 'districtName',
  DistrictId = 'districtId',
  StreetName = 'streetName',
  StreetId = 'streetId',
  HouseNameUnfound = 'houseNameUnfound',
  HouseId = 'houseId',
  OrientId = 'orientId',
  OrientName = 'orientName',
  SubwayId = 'subwayId',
  NewHouseId = 'newhouseId',
  CottageId = 'cottageId',
  SubwayName = 'subwayName',
  District = 'district',
  Status = 'status',
  Price = 'price',
  CreatedAt = 'createdAt',
  ItemCreatedAt = 'itemCreatedAt',
  DiffDays = 'diffDays',
  Username = 'username',
  Phone = 'phone',
  Year = 'year',
  Rooms = 'rooms',
  Currency = 'currency',
  Zoom = 'zoom',
  Center = 'center',
  Point = 'point',
  CommonAreaMin = 'commonAreaMin',
  CommonAreaMax = 'commonAreaMax',
  Period = 'period',
  AgencyName = 'agencyName',
  Speciality = 'speciality',
  PayCommission = 'payCommission',
}

export enum TableSearchPeriod {
  // за місяць
  //
  // за 3 міс
  //
  // За рік
  //
  // За 2 роки
  //
  // За весь час

  Month = 'month',
  ThreeMonths = 'threeMonths',
  Year = 'year',
  TwoYears = 'twoYears',
  AllTime = 'allTime',
}

export interface TableSearchInterface {
  [TableSearchParam.Page]: number;
  [TableSearchParam.PageSize]: number;
  [TableSearchParam.Address]: string;
  [TableSearchParam.CityName]: string;
  [TableSearchParam.CityId]: number;
  [TableSearchParam.StreetId]: number;
  [TableSearchParam.DistrictId]: number;
  [TableSearchParam.RegionName]: string;
  [TableSearchParam.RegionId]: number;
  [TableSearchParam.City]: number;
  [TableSearchParam.DistrictName]: string;
  [TableSearchParam.District]: number;
  [TableSearchParam.Id]: number;
  [TableSearchParam.Rieltor]: number;
  [TableSearchParam.RieltorName]: string;
  [TableSearchParam.Office]: number;
  [TableSearchParam.ItemType]: ItemType;
  [TableSearchParam.OperType]: OperType;
  [TableSearchParam.Mode]: string;
  [TableSearchParam.SubMode]: string;
  [TableSearchParam.Status]: OfferStatus;
  [TableSearchParam.Sort]: string;
  [TableSearchParam.Price]: string;
  [TableSearchParam.Username]: string;
  [TableSearchParam.Phone]: string;
  [TableSearchParam.Year]: number;
  [TableSearchParam.Rooms]: number;
  [TableSearchParam.Currency]: number;
  [TableSearchParam.Zoom]: number;
  [TableSearchParam.Center]: string;
  [TableSearchParam.Point]: string;
  [TableSearchParam.OrientId]: number;
  [TableSearchParam.SubwayId]: number;
  [TableSearchParam.NewHouseId]: number;
  [TableSearchParam.CottageId]: number;
  [TableSearchParam.HouseNameUnfound]: string;
  [TableSearchParam.CommonAreaMin]: number;
  [TableSearchParam.CommonAreaMax]: number;
  [TableSearchParam.AgencyName]: string;
  [TableSearchParam.Speciality]: string;
  [TableSearchParam.PayCommission]: number;
}

const normalizeQueryParams = (queryParams: any, defaultMode: string, defaultStatus?: number): Partial<TableSearchInterface> => {
  const {
    [TableSearchParam.Status]: status,
    [TableSearchParam.Page]: page,
    [TableSearchParam.PageSize]: pageSize,
    [TableSearchParam.OperType]: operType,
    [TableSearchParam.ItemType]: itemType,
    [TableSearchParam.Mode]: mode,
    ...rest
  } = queryParams;

  const normalizedMode = mode || defaultMode;

  const parsedPage = toNumber(page);
  const normalizedPage = !isNaN(parsedPage)
    ? parsedPage
    : FIRST_PAGE;

  const parsedPageSize = toNumber(pageSize);
  const normalizedPageSize = !isNaN(parsedPageSize)
    ? parsedPageSize
    : DEFAULT_PAGE_SIZE;

  const parsedStatus = toNumber(status);
  const normalizedStatus = !isNaN(parsedStatus) ? parsedStatus : defaultStatus;

  const parsedOperType = toNumber(operType);
  const normalizedOperType = !isNaN(parsedOperType)
    ? parsedOperType
    : undefined;

  const parsedItemType = toNumber(itemType);
  const normalizedItemType = !isNaN(parsedItemType)
    ? parsedItemType
    : undefined;

  return {
    [TableSearchParam.Page]: normalizedPage,
    [TableSearchParam.PageSize]: normalizedPageSize,
    [TableSearchParam.Status]: normalizedStatus,
    [TableSearchParam.OperType]: normalizedOperType,
    [TableSearchParam.ItemType]: normalizedItemType,
    [TableSearchParam.Mode]: normalizedMode,
    ...rest,
  };
};

export const getSearchParamsFromLocation = (location: any, defaultMode: string, defaultStatus?: number) => {
  const {
    [QueryParam.To]: to,
    [QueryParam.AlertType]: alertType,
    [QueryParam.AlertPayload]: alertPayload,
    [QueryParam.Referral]: referral,
    ...params
  } = parseLocationSearch(location);

  return normalizeQueryParams(params, defaultMode, defaultStatus);
};

interface TableModeResult {
  tableMode: string;
  isAdminMode: boolean;
}

export const useTableMode = (searchParams: Partial<TableSearchInterface>): TableModeResult => {
  const user = useCurrentUser();
  if (!user) {
    return {
      tableMode: DealsMode.List,
      isAdminMode: false,
    };
  }

  return {
    tableMode: searchParams[TableSearchParam.Mode] ?? '',
    isAdminMode: user.roleId === RoleId.AgencyAdmin,
  };
};

export const useTableSort = (searchParams: Partial<TableSearchInterface>, defaultSort: string = 'createdAt'): [boolean, string] => {
  const {sort} = searchParams;
  if (sort === undefined) {
    return [false, defaultSort];
  }
  const s: any = sort?.split('-');
  if (s.length > 1) {
    return [false, s[1]];
  } else {
    return [true, s[0]];
  }
};
type MultipleParamsChange = {[K in TableSearchParam]?: any};

const clearParamsOnModeChange = (nextSearchParams: Partial<TableSearchInterface>) => {
  delete nextSearchParams[TableSearchParam.Id];
  delete nextSearchParams[TableSearchParam.ItemType];
  delete nextSearchParams[TableSearchParam.OperType];
  delete nextSearchParams[TableSearchParam.CityName];
  delete nextSearchParams[TableSearchParam.City];
  delete nextSearchParams[TableSearchParam.District];
  delete nextSearchParams[TableSearchParam.DistrictName];
  delete nextSearchParams[TableSearchParam.Address];
  delete nextSearchParams[TableSearchParam.RieltorName];
  delete nextSearchParams[TableSearchParam.AgencyName];
  delete nextSearchParams[TableSearchParam.Speciality];
  delete nextSearchParams[TableSearchParam.Phone];
  delete nextSearchParams[TableSearchParam.Sort];
  delete nextSearchParams[TableSearchParam.PayCommission];
  delete nextSearchParams[TableSearchParam.Rooms];
  delete nextSearchParams[TableSearchParam.Status];
  delete nextSearchParams[TableSearchParam.Rieltor];
  delete nextSearchParams[TableSearchParam.HouseNameUnfound];
  delete nextSearchParams[TableSearchParam.SubMode];
};
interface SearchValuesResult {
  searchInputsValues: Partial<TableSearchInterface>;
  handleSearchInputChange: (param: TableSearchParam) => any;
  handleParamChange: (param: TableSearchParam, value: any, replace?: boolean) => void;
  handleParamsChange: (params: MultipleParamsChange, replace?: boolean) => void;
  handleOrderChange: (newOrderKey: string, newOrderValue: boolean) => void;
  handleModeChange: (mode: string) => void;
  handleSubModeChange: (subMode: string) => void;
  handlePageChange: (event: unknown, nextPage: number) => void;
  handlePageSizeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultipleValueParamChange: (param: TableSearchParam, value: any) => void;
}

// Getter for search params
export const useSearchParams = (defaultMode: string, defaultStatus?: number): Partial<TableSearchInterface> => {
  const location = useLocation();

  return useMemo(
    () => {
      return getSearchParamsFromLocation(location.search, defaultMode, defaultStatus);
    },
    [location, defaultMode]
  );
};

// Setter for search params
export const useSearchInputParams = (
  searchParams: Partial<TableSearchInterface>,
  urlBuilder: (params: any) => string
): SearchValuesResult => {
  const history = useHistory();

  // Copy of searchParams to handle search input values
  const [searchInputsValues, setSearchInputsValues] = useState(searchParams);

  const handleParamsChange = useCallback(
    (params: MultipleParamsChange, replace: boolean = false): void => {
      const nextSearchParams = {
        ...searchParams,
        ...params,
      };

      // Clear search inputs on Mode change
      if (Object.keys(params).includes(TableSearchParam.Mode)) {
        clearParamsOnModeChange(nextSearchParams);
      }

      if (replace) {
        history.replace(urlBuilder(nextSearchParams));
      } else {
        history.push(urlBuilder(nextSearchParams));
      }
    }, [searchParams, urlBuilder, history]
  );

  const handleMultipleValueParamChange = useCallback(
    (param: TableSearchParam, value: any): void => {
      const nextSearchParams: any = {
        ...searchParams,
      };

      if (param !== TableSearchParam.Page) {
        nextSearchParams[TableSearchParam.Page] = FIRST_PAGE;
      }

      let currentValue = nextSearchParams[param];

      if (currentValue && !Array.isArray(currentValue)) {
        currentValue = [currentValue];
      } else {
        // currentValue = [value];
      }

      if (currentValue) {
        if (currentValue.includes(value.toString())) {
          nextSearchParams[param] = currentValue.filter((v: any) => v !== value.toString());
        } else {
          nextSearchParams[param] = [...currentValue, value.toString()];
        }
      } else {
        nextSearchParams[param] = [value];
      }

      history.push(urlBuilder(nextSearchParams));
    }, [searchParams, urlBuilder, history]
  );

  const handleParamChange = useCallback(
    (param: TableSearchParam, value: any, replace: boolean = false): void => {
      const nextSearchParams = {
        ...searchParams,
        [param]: value,
      };

      if (param !== TableSearchParam.Page) {
        nextSearchParams[TableSearchParam.Page] = FIRST_PAGE;
      }

      // Clear search inputs on Mode change
      if (param === TableSearchParam.Mode) {
        clearParamsOnModeChange(nextSearchParams);
      }

      if (replace) {
        history.replace(urlBuilder(nextSearchParams));
      } else {
        history.push(urlBuilder(nextSearchParams));
      }
    },
    [searchParams, urlBuilder, history]
  );

  const handleParamChangeDebounced = useCallback(
    debounce(handleParamChange, 500),
    [handleParamChange]
  );

  const handleSearchInputChange = useCallback(
    (param: TableSearchParam) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextSearchInputValues = {
        ...searchInputsValues,
        [param]: event.target.value,
      };

      setSearchInputsValues(nextSearchInputValues);

      handleParamChangeDebounced(param, event.target.value);
    },
    [searchInputsValues, handleParamChangeDebounced]
  );

  const handleOrderChange = (newOrderKey: string, newOrderValue: boolean) => {
    handleParamChange(TableSearchParam.Sort, (!newOrderValue ? '-' : '') + newOrderKey);
  };

  const handleModeChange = (mode: string) => {
    handleParamChange(TableSearchParam.Mode, mode);
  };

  const handleSubModeChange = (subMode: string) => {
    handleParamChange(TableSearchParam.SubMode, subMode);
  };

  const handlePageChange = (event: unknown, nextPage: number) => {
    handleParamChange(TableSearchParam.Page, nextPage + 1);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleParamChange(TableSearchParam.PageSize, event.target.value);
  };

  useEffect(() => {
    setSearchInputsValues(searchParams);
  }, [searchParams]);

  return {
    searchInputsValues,
    handleSearchInputChange,
    handleParamsChange,
    handleParamChange,
    handleOrderChange,
    handleModeChange,
    handleSubModeChange,
    handlePageChange,
    handlePageSizeChange,
    handleMultipleValueParamChange
  };
};
